import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import { FieldArray as FormikFieldArray, Form as FormikForm, Formik } from 'formik';
import * as React from 'react';
import { uid } from 'react-uid';
import * as Yup from 'yup';
import { ConnectorFormat, ConnectorType, PowerType } from '../../../app/ApiGen';
import type { InputComponentProps } from '../../../design-system';
import { pluralise } from '../../../utils/format';
import { createSchema, type FormFields, toChangeEvent } from '../../../utils/formUtils';
import { Actions } from '../../ConnectorItem/styled';
import { Dialog } from '../../Dialog';
import { TextField } from '../../TextField';
import { type ConnectorFormFields, ConnectorSection, type ConnectorsForm } from './ConnectorSection';
import { FormatInput } from './Format';
import { PowerTypeInput } from './PowerType';
import { StandardInput } from './Standard';

const defaultConnector: ConnectorFormFields = {
  connectorType: '' as ConnectorType,
  connectorFormat: '' as ConnectorFormat,
  maxAmperage: 32,
  maxVoltage: 230,
  powerType: '' as PowerType,
};

const connectorFields: FormFields<ConnectorFormFields> = {
  connectorType: { id: 'connectorType', label: 'Standard', inputComponent: StandardInput },
  connectorFormat: { id: 'connectorFormat', label: 'Format', inputComponent: FormatInput },
  powerType: { id: 'powerType', label: 'Power type', inputComponent: PowerTypeInput },
  maxAmperage: {
    id: 'maxAmperage',
    label: 'Max Current',
    inputProps: { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">A</InputAdornment> } },
  },
  maxVoltage: {
    id: 'maxVoltage',
    label: 'Max Voltage',
    inputProps: { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">V</InputAdornment> } },
  },
};

/* eslint no-template-curly-in-string: "off" --- Yup messages can be customised using template curlies in strings. */
const connectorValidationSchema = createSchema<ConnectorFormFields>({
  connectorType: Yup.string<ConnectorType>().required('Connector standard is required'),
  connectorFormat: Yup.string<ConnectorFormat>().required('Connector format is required'),
  maxAmperage: Yup.number()
    .typeError('Connector current must be a number')
    .min(6, 'Connector current must be between ${min} and 500 amps')
    .max(500, 'Connector current must be between 6 and ${max} amps')
    .required('Connector current is required'),
  maxVoltage: Yup.number()
    .typeError('Connector voltage must be a number')
    .min(0, 'Connector voltage must be between ${min} and 920 volts')
    .max(920, 'Connector voltage must be between 0 and ${max} volts')
    .required('Connector voltage is required'),
  powerType: Yup.string<PowerType>().required('Connector power type is required'),
});
const validationSchema = Yup.object().shape({
  connectors: Yup.array().of(connectorValidationSchema).required('Charge point must have at least one connector'),
});

export const ConnectorsInput: React.FC<InputComponentProps<ConnectorFormFields[]>> = ({
  disabled,
  error,
  helperText,
  name,
  onBlur,
  onChange,
  value,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const onSubmit = ({ connectors }: ConnectorsForm) => {
    onChange(name)(toChangeEvent<ConnectorFormFields[]>(new Event('change'), connectors));
    setIsOpen(false);
  };

  return (
    <>
      <TextField
        InputProps={{ readOnly: true, style: { cursor: 'pointer' }, inputProps: { style: { cursor: 'pointer' } } }}
        disabled={disabled}
        error={error}
        fullWidth
        helperText={helperText}
        onBlur={onBlur}
        onChange={onChange}
        onClick={() => setIsOpen(true)}
        value={pluralise(value.length, 'connector')}
      />
      <Dialog isOpen={isOpen} onClose={() => setIsOpen(false)} title="Configure connectors" hasCloseButton={false}>
        <Formik<ConnectorsForm>
          initialValues={{ connectors: value ?? [] }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values, errors, touched }) => (
            <FormikForm autoComplete="off" style={{ padding: '24px' }}>
              <FormikFieldArray name={name}>
                {(arrayHelpers) => (
                  <>
                    {values.connectors.map((_, idx: number) => (
                      <ConnectorSection
                        arrayHelpers={arrayHelpers}
                        errors={errors}
                        fields={connectorFields}
                        key={uid(name, idx)}
                        name={name}
                        sectionId={idx}
                        touched={touched}
                      />
                    ))}
                    <Box mt={values.connectors.length > 0 ? 4 : 0} mb={3}>
                      <Button variant="outlined" fullWidth onClick={() => arrayHelpers.push(defaultConnector)}>
                        {values.connectors.length > 0 ? 'Add another connector' : 'Add connector'}
                      </Button>
                    </Box>
                  </>
                )}
              </FormikFieldArray>
              <Actions style={{ display: 'flex' }}>
                <Button color="inherit" onClick={() => setIsOpen(false)} variant="contained">
                  Discard
                </Button>
                <Box ml="auto" />
                <Button color="primary" type="submit" variant="contained">
                  Done
                </Button>
              </Actions>
            </FormikForm>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
